/**
 * Shared phenotype + locus catalogue used by Calculator and Flock pages.
 *
 * Both pages render the same 7 phenotype pickers and the same locus rows
 * (advanced view), so the catalogue lives here. The genetics engine on
 * the backend remains the single source of truth for inheritance.
 */
const AUTO_RECESSIVE = [
  { value: "no", label: "Wild-type (no factor)" },
  { value: "split", label: "Split (carrier)" },
  { value: "expressed", label: "Visual (double factor)" },
];
const AUTO_DOMINANT = [
  { value: "no", label: "No factor" },
  { value: "single", label: "Single factor" },
  { value: "double", label: "Double factor" },
];
const INC_DOMINANT = AUTO_DOMINANT;
const SEX_LINKED = [
  { value: "no", label: "No factor" },
  { value: "split", label: "Split (cocks only)" },
  { value: "expressed", label: "Visual" },
];

export const LOCI_UI = [
  { id: "base", label: "Base Color", help: "Blue (bb) is recessive to Green.", options: AUTO_RECESSIVE },
  { id: "dark", label: "Dark Factor", help: "0/1/2 doses = Light/Dark/Olive (or Sky/Cobalt/Mauve).", options: INC_DOMINANT },
  { id: "violet", label: "Violet Factor", options: INC_DOMINANT },
  { id: "grey", label: "Grey", options: AUTO_DOMINANT },
  { id: "spangle", label: "Spangle", options: INC_DOMINANT },
  { id: "dom_pied", label: "Dominant Pied", options: AUTO_DOMINANT },
  { id: "rec_pied", label: "Recessive Pied", options: AUTO_RECESSIVE },
  { id: "crest", label: "Crested", options: INC_DOMINANT },
  { id: "yellowface", label: "Yellowface II / Goldenface", options: INC_DOMINANT },
  { id: "dilution", label: "Dilute", options: AUTO_RECESSIVE },
  { id: "ino", label: "Ino", help: "Lutino/Albino. Sex-linked: hens can only be no/visual.", options: SEX_LINKED, sexLinked: true },
  { id: "cinnamon", label: "Cinnamon", options: SEX_LINKED, sexLinked: true },
  { id: "opaline", label: "Opaline", options: SEX_LINKED, sexLinked: true },
];

export const PHENOTYPES = [
  {
    id: "shade",
    label: "Body Shade",
    help: "Overall body colour — pick the shade that matches your bird.",
    options: [
      { id: "light-green",  label: "Light Green",  swatch: "#7BC043", mutationId: "normal-green",   loci: { base:"no",        dark:"no",     grey:"no",     violet:"no" } },
      { id: "dark-green",   label: "Dark Green",   swatch: "#3F8C3A", mutationId: "dark-green",     loci: { base:"no",        dark:"single", grey:"no",     violet:"no" } },
      { id: "olive",        label: "Olive",        swatch: "#5C5A2A", mutationId: "olive",          loci: { base:"no",        dark:"double", grey:"no",     violet:"no" } },
      { id: "grey-green",   label: "Grey Green",   swatch: "#7F8C5B", mutationId: "grey-green",     loci: { base:"no",        dark:"no",     grey:"single", violet:"no" } },
      { id: "sky-blue",     label: "Sky Blue",     swatch: "#7FBDD9", mutationId: "sky-blue",       loci: { base:"expressed", dark:"no",     grey:"no",     violet:"no" } },
      { id: "cobalt",       label: "Cobalt",       swatch: "#3A6BA8", mutationId: "cobalt",         loci: { base:"expressed", dark:"single", grey:"no",     violet:"no" } },
      { id: "mauve",        label: "Mauve",        swatch: "#6B5C7A", mutationId: "mauve",          loci: { base:"expressed", dark:"double", grey:"no",     violet:"no" } },
      { id: "grey",         label: "Grey",         swatch: "#8C8C8E", mutationId: "grey",           loci: { base:"expressed", dark:"no",     grey:"single", violet:"no" } },
      { id: "violet-sky",   label: "Violet Sky",   swatch: "#7F6BC3", mutationId: "violet",         loci: { base:"expressed", dark:"no",     grey:"no",     violet:"single" } },
      { id: "violet",       label: "Violet Cobalt",swatch: "#5B4A8B", mutationId: "violet",         loci: { base:"expressed", dark:"single", grey:"no",     violet:"single" } },
    ],
  },
  {
    id: "face",
    label: "Face / Ground",
    help: "Colour of the face mask and ground colour.",
    options: [
      { id: "white-yellow", label: "Standard",        swatch: "#FAF6E5", mutationId: null,           loci: { yellowface:"no" } },
      { id: "yellowface",   label: "Yellowface II",   swatch: "#F0D75A", mutationId: "yellowface-2", loci: { yellowface:"single" } },
      { id: "goldenface",   label: "Goldenface DF",   swatch: "#E8C545", mutationId: "yellowface-1", loci: { yellowface:"double" } },
    ],
  },
  {
    id: "variety",
    label: "Wing Markings",
    help: "Pattern of wing markings.",
    options: [
      { id: "normal-wings", label: "Normal",          swatch: "#1F1F1F", mutationId: null,        loci: { opaline:"no",         cinnamon:"no",        spangle:"no" } },
      { id: "opaline",      label: "Opaline",         swatch: "#7B6E96", mutationId: "opaline",   loci: { opaline:"expressed",  cinnamon:"no",        spangle:"no" } },
      { id: "cinnamon",     label: "Cinnamon",        swatch: "#A47844", mutationId: "cinnamon",  loci: { opaline:"no",         cinnamon:"expressed", spangle:"no" } },
      { id: "op-cinn",      label: "Opaline-Cinnamon",swatch: "#9C7E72", mutationId: "cinnamon",  loci: { opaline:"expressed",  cinnamon:"expressed", spangle:"no" } },
      { id: "spangle-sf",   label: "Spangle (SF)",    swatch: "#D5C9A8", mutationId: "spangle",   loci: { opaline:"no",         cinnamon:"no",        spangle:"single" } },
      { id: "spangle-df",   label: "Spangle (DF)",    swatch: "#F0E8C5", mutationId: "spangle",   loci: { opaline:"no",         cinnamon:"no",        spangle:"double" } },
    ],
  },
  {
    id: "dilution",
    label: "Dilution",
    help: "Reduces body-colour depth or wing markings.",
    options: [
      { id: "no-dilution",  label: "Normal",          swatch: "#1F1F1F", mutationId: null,         loci: { dilution:"no" } },
      { id: "clearwing",    label: "Clearwing",       swatch: "#DDD3A6", mutationId: "clearwing",  loci: { dilution:"split" } },
      { id: "dilute",       label: "Dilute / Suffused", swatch: "#F0DA9F", mutationId: "dilute",   loci: { dilution:"expressed" } },
    ],
  },
  {
    id: "pied",
    label: "Pied",
    help: "Random patches of clear feathering.",
    options: [
      { id: "no-pied",      label: "No Pied",         swatch: "#1F1F1F", mutationId: null,         loci: { dom_pied:"no",     rec_pied:"no" } },
      { id: "dom-pied",     label: "Dominant Pied",   swatch: "#FFD86B", mutationId: null,         loci: { dom_pied:"single", rec_pied:"no" } },
      { id: "rec-pied",     label: "Recessive Pied",  swatch: "#F5C04B", mutationId: null,         loci: { dom_pied:"no",     rec_pied:"expressed" } },
    ],
  },
  {
    id: "ino",
    label: "Ino",
    help: "Removes all dark pigment — Lutino (yellow) or Albino (white).",
    options: [
      { id: "no-ino",       label: "No Ino",          swatch: "#1F1F1F", mutationId: null,         loci: { ino:"no" } },
      { id: "lutino",       label: "Lutino",          swatch: "#FFEB7A", mutationId: "lutino",     loci: { ino:"expressed" } },
      { id: "albino",       label: "Albino",          swatch: "#FAFAFA", mutationId: "albino",     loci: { ino:"expressed", base:"expressed" } },
    ],
  },
  {
    id: "crested",
    label: "Crested",
    help: "Tuft or full crown of feathers on the head.",
    options: [
      { id: "no-crest",     label: "No Crest",        swatch: "#1F1F1F", mutationId: null,         loci: { crest:"no" } },
      { id: "tufted",       label: "Tufted (SF)",     swatch: "#D4BBA1", mutationId: "crested",    loci: { crest:"single" } },
      { id: "full-crested", label: "Full Crested (DF)", swatch: "#C8A87F", mutationId: "crested",  loci: { crest:"double" } },
    ],
  },
];

export function defaultOptions() {
  const o = {};
  LOCI_UI.forEach((l) => {
    o[l.id] = { state: "no" };
  });
  return o;
}

export function applyPhenotype(parent, option, sex) {
  const newOpts = { ...parent.options };
  for (const [locusId, value] of Object.entries(option.loci)) {
    if (value === null || value === undefined) continue;
    const locusMeta = LOCI_UI.find((l) => l.id === locusId);
    let v = value;
    if (locusMeta?.sexLinked && sex === "female" && v === "split") v = "no";
    newOpts[locusId] = { state: v };
  }
  return { ...parent, options: newOpts };
}

export function findActiveOption(parent, category) {
  for (const opt of category.options) {
    const allMatch = Object.entries(opt.loci).every(([k, v]) => {
      if (v === null || v === undefined) return true;
      return (parent.options[k]?.state || "no") === v;
    });
    if (allMatch) return opt.id;
  }
  return null;
}

/** Build a compact human label like "Cobalt Opaline Cinnamon ♂" */
export function summarizePhenotype(parent) {
  const parts = [];
  for (const cat of PHENOTYPES) {
    const id = findActiveOption(parent, cat);
    if (!id) continue;
    const opt = cat.options.find((o) => o.id === id);
    if (!opt) continue;
    // Skip "no/normal/standard" placeholder options
    if (/^no[- ]|^standard$|^normal$/i.test(opt.label) || opt.id.startsWith("no-")) continue;
    parts.push(opt.label);
  }
  if (parts.length === 0) parts.push("Light Green");
  return parts.join(" · ");
}
